import React from 'react';
import { Link } from 'react-router-dom';

/**
 * App Store Connect に登録している自動更新サブスクリプションの内容。
 * StoreKit から商品を取得できない場合でも Guideline 3.1.2(c) の必須項目
 * （名称・購読期間・期間中に提供される内容）が必ず画面に出るよう、
 * ここを唯一の正とする。価格だけは地域差があるため StoreKit の値を使う。
 */
export const IAP_PLAN = {
  name: 'JTALK プレミアム（月額）',
  periodLabel: '1か月',
  benefits: [
    'ライブルームを時間制限なしで利用（無料プランは1時間まで）',
    'プレミアム限定トークルームへの入室',
    'AI翻訳・AI添削が無制限',
    'プロフィールにプレミアムバッジ',
  ],
};

const PERIOD_UNIT_LABELS = {
  day: '日',
  week: '週間',
  month: 'か月',
  year: '年',
};

// StoreKit の期間は環境によって形が違う:
//   - { unitString: 'month', numberOfUnits: 1 }
//   - { unit: 2, numberOfUnits: 1 }（0=day, 1=week, 2=month, 3=year）
//   - 'P1M' のような ISO 8601 文字列
const PERIOD_UNIT_NAMES = ['day', 'week', 'month', 'year'];
const ISO_UNITS = { D: 'day', W: 'week', M: 'month', Y: 'year' };

function parsePeriod(raw) {
  if (!raw) return null;

  if (typeof raw === 'string') {
    const m = /^P(\d+)([DWMY])$/.exec(raw.trim().toUpperCase());
    if (!m) return null;
    return { unit: ISO_UNITS[m[2]], count: Number(m[1]) || 1 };
  }

  const rawUnit = String(raw.unitString || raw.unit || '').toLowerCase();
  const unit = PERIOD_UNIT_LABELS[rawUnit]
    ? rawUnit
    : PERIOD_UNIT_NAMES[Number(raw.unit)] || '';
  if (!PERIOD_UNIT_LABELS[unit]) return null;

  const numberOfUnits = Number(raw.numberOfUnits);
  return {
    unit,
    count: Number.isFinite(numberOfUnits) && numberOfUnits > 0 ? numberOfUnits : 1,
  };
}

/** StoreKit の期間が読めればそれを、読めなければ登録済みプランの期間を返す */
export function getIapPeriodLabel(product) {
  const parsed = parsePeriod(product?.subscriptionPeriod);
  if (!parsed) return IAP_PLAN.periodLabel;
  return `${parsed.count}${PERIOD_UNIT_LABELS[parsed.unit]}`;
}

/**
 * Apple は購入操作の前に、商品名・購読期間・期間中に提供される内容・価格・
 * EULA / プライバシーポリシーへのリンクを同一画面に出すことを求めている
 * （Guideline 3.1.2(c)）。iOS の購入導線には必ずこのコンポーネントを置く。
 */
export default function IapSubscriptionDetails({
  product,
  loading = false,
  error = '',
  onRetry,
  compact = false,
}) {
  const periodLabel = getIapPeriodLabel(product);
  const title = product?.title || IAP_PLAN.name;
  const price = product?.priceString || '';

  return (
    <div
      style={{ ...S.card, ...(compact ? S.cardCompact : {}) }}
      aria-label="サブスクリプション情報"
    >
      <p style={S.eyebrow}>App Store サブスクリプション</p>
      <div style={S.row}>
        <span style={S.label}>商品名</span>
        <strong style={S.value}>{title}</strong>
      </div>
      <div style={S.row}>
        <span style={S.label}>購読期間</span>
        <strong style={S.value}>{periodLabel}（自動更新）</strong>
      </div>
      <div style={S.row}>
        <span style={S.label}>{`価格（${periodLabel}ごと）`}</span>
        <strong style={S.price}>
          {price ? `${price} / ${periodLabel}` : (loading ? '読み込み中…' : '—')}
        </strong>
      </div>

      <div style={S.content}>
        <p style={S.contentHead}>{`${periodLabel}ごとの購読期間中に利用できる内容`}</p>
        <ul style={S.benefitList}>
          {IAP_PLAN.benefits.map((b) => (
            <li key={b} style={S.benefit}>{b}</li>
          ))}
        </ul>
      </div>

      {loading ? (
        <p style={S.notice} role="status">App Store から最新の価格を読み込んでいます…</p>
      ) : null}
      {error ? (
        <div style={S.errorBox} role="alert">
          <p style={S.errorText}>{error}。通信環境をご確認のうえ、もう一度お試しください。</p>
          {onRetry ? (
            <button data-control="secondary" data-control-id="IapSubscriptionDetails-retry" type="button" style={S.retry} onClick={onRetry}>
              もう一度読み込む
            </button>
          ) : null}
        </div>
      ) : null}

      <p style={S.legal}>
        {`購入確認時に Apple ID へ請求されます。現在の購読期間終了の24時間前までに解約しない限り、同じ価格で${periodLabel}ごとに自動更新されます。購入後は iPhone / iPad の「設定 > Apple ID > サブスクリプション」からいつでも管理・解約できます。`}
      </p>
      <div style={S.links}>
        <Link to="/terms" style={S.link}>利用規約（EULA）</Link>
        <a href="/privacy.html" style={S.link}>プライバシーポリシー</a>
      </div>
    </div>
  );
}

const S = {
  card: {
    width: '100%',
    boxSizing: 'border-box',
    margin: '0 0 14px',
    padding: 16,
    borderRadius: 'var(--r-card)',
    background: 'var(--surface)',
    border: '1px solid var(--primary-light)',
    boxShadow: 'var(--shadow-sm)',
    textAlign: 'left',
  },
  cardCompact: {
    padding: 14,
  },
  eyebrow: {
    margin: '0 0 7px',
    color: 'var(--primary-dark)',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.06em',
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    padding: '6px 0',
  },
  label: {
    color: 'var(--text-secondary)',
    fontSize: 12,
    lineHeight: 1.4,
    flexShrink: 0,
  },
  value: {
    color: 'var(--text)',
    fontSize: 14,
    lineHeight: 1.4,
    textAlign: 'right',
  },
  price: {
    color: 'var(--primary-dark)',
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.4,
    textAlign: 'right',
  },
  content: {
    margin: '10px 0 0',
    padding: '10px 12px',
    borderRadius: 'var(--r-input)',
    background: 'var(--primary-ultra)',
  },
  contentHead: {
    margin: '0 0 6px',
    color: 'var(--text)',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.5,
  },
  benefitList: {
    margin: 0,
    padding: '0 0 0 18px',
    color: 'var(--text-secondary)',
    fontSize: 12,
    lineHeight: 1.7,
  },
  benefit: {
    margin: 0,
  },
  notice: {
    margin: '10px 0 0',
    color: 'var(--text-secondary)',
    fontSize: 12,
    lineHeight: 1.5,
  },
  errorBox: {
    marginTop: 10,
    padding: '10px 12px',
    borderRadius: 'var(--r-input)',
    background: 'var(--accent-light)',
  },
  errorText: {
    margin: 0,
    color: 'var(--text-secondary)',
    fontSize: 12,
    lineHeight: 1.55,
  },
  retry: {
    marginTop: 8,
    padding: '7px 14px',
    border: '1px solid var(--primary)',
    borderRadius: 'var(--r-btn)',
    background: 'transparent',
    color: 'var(--primary-dark)',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 800,
    cursor: 'pointer',
  },
  legal: {
    margin: '12px 0 0',
    color: 'var(--text-muted)',
    fontSize: 11,
    lineHeight: 1.55,
  },
  links: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 10,
    fontSize: 12,
  },
  link: {
    color: 'var(--primary-dark)',
    fontWeight: 700,
    textDecoration: 'underline',
  },
};
